<template>
  <form class="create-or-edit-model-form" @submit.prevent.stop="submit">
    <div class="form-row">
      <p-text-field v-model.trim="name" :disabled="isRequestPending || !$hasPermission('administration')" :label="'Name'" />
    </div>
    <div class="form-row">
      <p-multiselect
        v-model.trim="provider"
        :disabled="isRequestPending || !$hasPermission('administration')"
        :options="providerOptions"
        :label="'Provider'"
        :searchable="false"
        placeholder=""
      />
    </div>
    <div class="form-row">
      <p-multiselect
        v-model="capability"
        :options="providerCapabilities"
        :disabled="isRequestPending || !$hasPermission('administration')"
        label="Capability"
        :searchable="false"
      ></p-multiselect>
    </div>
    <div class="form-row">
      <p-text-field v-model="version" :disabled="isRequestPending || !$hasPermission('administration')" :label="'Version'" />
    </div>
    <div class="form-row">
      <p-multiselect
        v-model="organizations"
        :options="organizationsOptions"
        :get-label-callback="getOrganizationName"
        :disabled="isRequestPending || !$hasPermission('administration')"
        label="Organizations (specify organizations that can use this model)"
        placeholder="Select organizations"
        :searchable="true"
        :multiple="true"
        @remove="onRemoveOrganization"
      ></p-multiselect>
    </div>
    <div class="form-row">
      <p-text-field v-model="description" :disabled="isRequestPending || !$hasPermission('administration')" :label="'Description'" :multiline="true" :rows="3" />
    </div>
    <div class="form-row code">
      <label>Configuration</label>
      <p-code v-model="config" :read-only="isRequestPending || !$hasPermission('administration')" :language="'json'"></p-code>
    </div>
    <div>
      <p-button type="button" :disabled="isRequestPending" @click.prevent="$router.back()">Cancel</p-button>
      <p-button v-if="$hasPermission('administration')" color="primary" type="submit" :disabled="isRequestPending">Save</p-button>
    </div>
  </form>
</template>

<script>
import { mapState } from 'vuex';

import Multiselect from '@/components/common/Multiselect';
import TextField from '@/components/common/TextField';
import Button from '@/components/common/Button';
import Code from '@/components/common/Code';

export default {
  components: {
    'p-text-field': TextField,
    'p-multiselect': Multiselect,
    'p-button': Button,
    'p-code': Code
  },
  props: {
    item: {
      type: Object,
      default: () => {}
    },
    isRequestPending: {
      type: Boolean,
      default: false
    }
  },
  data() {
    if (this.item) {
      return {
        provider: this.item.provider,
        name: this.item.name,
        capability: this.item.capability,
        description: this.item.description,
        version: this.item.version,
        organizations: [...(this.item.organizations ?? [])],
        config: (this.item.config && JSON.stringify(JSON.parse(this.item.config), null, 2)) || ''
      };
    } else {
      return {
        provider: '',
        name: '',
        capability: '',
        description: '',
        version: '',
        organizations: [],
        config: JSON.stringify({ property: 'value' }, null, 2)
      };
    }
  },
  computed: {
    ...mapState({
      providers: s => s.nlp.providers || {},
      organizationsCollection: s => s.organizations.collection
    }),
    providerOptions() {
      return this.providers.map(p => p.name);
    },
    modelProvider() {
      return this.providers.find(p => p.name == this.provider);
    },
    providerCapabilities() {
      return this.modelProvider?.capabilities.map(c => c.type);
    },
    organizationsOptions() {
      return this.organizationsCollection.map(o => o.id) ?? [];
    }
  },
  async created() {
    const promises = [];

    if (!this.providers?.length) {
      promises.push(this.$store.dispatch('nlp/getConfigurations'));
    }

    if (!this.organizationsCollection?.length) {
      promises.push(this.$store.dispatch('organizations/getCollection'));
    }

    await Promise.all(promises);
  },
  methods: {
    onRemoveOrganization(removedId) {
      const org = this.organizationsCollection.find(o => o.id === removedId);
      if (org && (org.speechPostProcessingModel?.id === this.item.id || org.speechToTextService?.id === this.item.id)) {
        this.$toast.error({
          title: `Cannot delete ${org.name} from this model because it is in use.`,
          message: `You can change the ${org.name} speech recognition settings in the Organizations panel.`
        });
        this.$nextTick(() => {
          if (!this.organizations.includes(removedId)) {
            this.organizations = [...this.organizations, removedId];
          }
        });
        return;
      }
      this.organizations = this.organizations.filter(id => id !== removedId);
    },
    getOrganizationName(value) {
      return this.organizationsCollection.find(o => o.id === value)?.name ?? 'Unknown Organization';
    },
    async submit() {
      this.$emit('submit', {
        ...this.item,
        provider: this.provider,
        name: this.name,
        capability: this.capability,
        description: this.description,
        version: this.version,
        organizations: this.organizations,
        config: JSON.stringify(JSON.parse(this.config))
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.create-or-edit-model-form {
  padding: 2rem;
  background: var(--theme-surface);
  overflow-y: auto;

  label {
    font-weight: 500;
    font-size: 0.75rem;
    letter-spacing: 0.025em;
  }

  .code {
    min-height: 300px;
  }

  > * {
    &:not(:last-child) {
      margin-bottom: 1.5rem;
    }
    &:last-child {
      display: flex;
      justify-content: flex-end;
    }
  }
}
</style>
